import { fetchApi } from './api';
import type {
    VCAFilterOptions,
    VCAOverviewData,
    VCAVendorRow,
    VCATransactionRow,
    VCAVendorGroupRow,
    VCAGroupMember,
    VCAVendorGroupAggRow,
    VCACategoryDetail,
    VCAUnmappedAccount,
    VCAAccountWithMappings,
    VCAComment,
    PaginationInfo,
    VCATotals,
    VCATransactionTotals,
    VCACompanyOption,
} from '../types/vendorCostAnalysis';

function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function todayStamp(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const vendorCostAnalysisApi = {
    async getFilters(): Promise<VCAFilterOptions> {
        return fetchApi('/vendor-cost-analysis/filters');
    },

    async getOverview(params: {
        year: number;
        maxMonth: number;
        dateFrom: string;
        dateTo: string;
        company?: string;
        vendorID?: string;
        groupVendorID?: string;
        vendorIDs?: string[];
        groupVendorIDs?: string[];
        categoryCode?: string;
        trendMonths?: number;
        viewMode?: string;
    }): Promise<VCAOverviewData> {
        const sp = new URLSearchParams();
        sp.set('year', String(params.year));
        sp.set('maxMonth', String(params.maxMonth));
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.vendorID) sp.set('vendorID', params.vendorID);
        if (params.groupVendorID) sp.set('groupVendorID', params.groupVendorID);
        if (params.vendorIDs?.length) sp.set('vendorIDs', params.vendorIDs.join(','));
        if (params.groupVendorIDs?.length) sp.set('groupVendorIDs', params.groupVendorIDs.join(','));
        if (params.categoryCode) sp.set('categoryCode', params.categoryCode);
        if (params.trendMonths) sp.set('trendMonths', String(params.trendMonths));
        if (params.viewMode) sp.set('viewMode', params.viewMode);
        return fetchApi(`/vendor-cost-analysis/overview?${sp}`);
    },

    async getByVendor(params: {
        dateFrom: string;
        dateTo: string;
        company?: string;
        categoryCode?: string;
        vendorIDs?: string[];
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
    }): Promise<{ items: VCAVendorRow[]; pagination: PaginationInfo; totals: VCATotals }> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.categoryCode) sp.set('categoryCode', params.categoryCode);
        if (params.vendorIDs?.length) sp.set('vendorIDs', params.vendorIDs.join(','));
        if (params.sortBy) sp.set('sortBy', params.sortBy);
        if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));
        const res = await fetch(`/api/v1/vendor-cost-analysis/by-vendor?${sp}`);
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to fetch vendor data');
        return { items: body.data, pagination: body.pagination, totals: body.totals };
    },

    async getTransactions(params: {
        dateFrom: string;
        dateTo: string;
        company?: string;
        vendorID?: string;
        groupVendorID?: string;
        vendorIDs?: string[];
        groupVendorIDs?: string[];
        categoryCode?: string;
        accountID?: string;
        search?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
    }): Promise<{ items: VCATransactionRow[]; pagination: PaginationInfo; totals: VCATransactionTotals; companies: VCACompanyOption[] }> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.vendorID) sp.set('vendorID', params.vendorID);
        if (params.groupVendorID) sp.set('groupVendorID', params.groupVendorID);
        if (params.vendorIDs?.length) sp.set('vendorIDs', params.vendorIDs.join(','));
        if (params.groupVendorIDs?.length) sp.set('groupVendorIDs', params.groupVendorIDs.join(','));
        if (params.categoryCode) sp.set('categoryCode', params.categoryCode);
        if (params.accountID) sp.set('accountID', params.accountID);
        if (params.search) sp.set('search', params.search);
        if (params.sortBy) sp.set('sortBy', params.sortBy);
        if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));
        const res = await fetch(`/api/v1/vendor-cost-analysis/transactions?${sp}`);
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to fetch transactions');
        return { items: body.data, pagination: body.pagination, totals: body.totals, companies: body.companies || [] };
    },

    async getVendorGroups(params: {
        dateFrom: string;
        dateTo: string;
        search?: string;
        page?: number;
        limit?: number;
    }): Promise<{ items: VCAVendorGroupRow[]; pagination: PaginationInfo }> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.search) sp.set('search', params.search);
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));
        const res = await fetch(`/api/v1/vendor-cost-analysis/vendor-groups?${sp}`);
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to fetch vendor groups');
        return { items: body.data, pagination: body.pagination };
    },

    async getGroupMembers(groupId: number, params: {
        dateFrom: string;
        dateTo: string;
    }): Promise<VCAGroupMember[]> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        return fetchApi(`/vendor-cost-analysis/vendor-groups/${groupId}/members?${sp}`);
    },

    async getByVendorGroup(params: {
        dateFrom: string;
        dateTo: string;
        company?: string;
        categoryCode?: string;
        groupVendorIDs?: string[];
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
    }): Promise<{ items: VCAVendorGroupAggRow[]; pagination: PaginationInfo; totals: VCATotals }> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.categoryCode) sp.set('categoryCode', params.categoryCode);
        if (params.groupVendorIDs?.length) sp.set('groupVendorIDs', params.groupVendorIDs.join(','));
        if (params.sortBy) sp.set('sortBy', params.sortBy);
        if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));
        const res = await fetch(`/api/v1/vendor-cost-analysis/by-vendor-group?${sp}`);
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to fetch vendor group data');
        return { items: body.data, pagination: body.pagination, totals: body.totals };
    },

    async createGroupOverride(data: {
        vendorID: string;
        targetGroupVendorID: number | null;
        overrideType: 'MERGE' | 'SPLIT';
        reason: string;
    }): Promise<{ message: string }> {
        const res = await fetch('/api/v1/vendor-cost-analysis/vendor-groups/overrides', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to create override');
        return body.data;
    },

    async deleteGroupOverride(id: number): Promise<void> {
        const res = await fetch(`/api/v1/vendor-cost-analysis/vendor-groups/overrides/${id}`, {
            method: 'DELETE',
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to delete override');
    },

    async saveGroupNote(groupId: number, data: { description: string; groupName?: string }): Promise<void> {
        const res = await fetch(`/api/v1/vendor-cost-analysis/vendor-groups/${groupId}/notes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to save note');
    },

    // Category Settings
    async getCategories(params?: { includeUnmapped?: boolean; categoryCode?: string; mode?: 'all-accounts' }): Promise<{ categories: VCACategoryDetail[]; unmappedAccounts: VCAUnmappedAccount[]; mappedAccounts: VCAUnmappedAccount[]; allAccounts: VCAAccountWithMappings[] }> {
        const sp = new URLSearchParams();
        if (params?.includeUnmapped) sp.set('includeUnmapped', 'true');
        if (params?.categoryCode) sp.set('categoryCode', params.categoryCode);
        if (params?.mode) sp.set('mode', params.mode);
        const body = await fetchApi<{ categories: VCACategoryDetail[]; unmappedAccounts: VCAUnmappedAccount[]; mappedAccounts: VCAUnmappedAccount[]; allAccounts?: VCAAccountWithMappings[] }>(`/vendor-cost-analysis/categories?${sp}`);
        return { ...body, allAccounts: body.allAccounts || [] };
    },

    async saveCategory(data: { categoryCode: string; categoryName: string; categoryDescription?: string }): Promise<void> {
        const res = await fetch('/api/v1/vendor-cost-analysis/categories', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to save category');
    },

    async deleteCategory(code: string): Promise<void> {
        const res = await fetch(`/api/v1/vendor-cost-analysis/categories/${encodeURIComponent(code)}`, { method: 'DELETE' });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || body.error || 'Failed to delete category');
    },

    async saveAccountMapping(data: { accountID: string; categoryCode: string; action: 'add' | 'remove' }): Promise<void> {
        const res = await fetch('/api/v1/vendor-cost-analysis/account-mappings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to save mapping');
    },

    // Excel Exports
    async exportByVendor(params: {
        dateFrom: string;
        dateTo: string;
        company?: string;
        categoryCode?: string;
        vendorIDs?: string[];
        groupVendorIDs?: string[];
        viewMode: 'vendor' | 'vendorGroup';
    }): Promise<void> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        sp.set('viewMode', params.viewMode);
        if (params.company) sp.set('company', params.company);
        if (params.categoryCode) sp.set('categoryCode', params.categoryCode);
        if (params.vendorIDs?.length) sp.set('vendorIDs', params.vendorIDs.join(','));
        if (params.groupVendorIDs?.length) sp.set('groupVendorIDs', params.groupVendorIDs.join(','));
        const res = await fetch(`/api/v1/vendor-cost-analysis/by-vendor/export?${sp}`);
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        const filename = params.viewMode === 'vendorGroup'
            ? `VendorCostByGroup_${todayStamp()}.xlsx`
            : `VendorCostByVendor_${todayStamp()}.xlsx`;
        downloadBlob(await res.blob(), filename);
    },

    async exportTransactions(params: {
        dateFrom: string;
        dateTo: string;
        company?: string;
        vendorID?: string;
        groupVendorID?: string;
        vendorIDs?: string[];
        groupVendorIDs?: string[];
        categoryCode?: string;
        search?: string;
    }): Promise<void> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.vendorID) sp.set('vendorID', params.vendorID);
        if (params.groupVendorID) sp.set('groupVendorID', params.groupVendorID);
        if (params.vendorIDs?.length) sp.set('vendorIDs', params.vendorIDs.join(','));
        if (params.groupVendorIDs?.length) sp.set('groupVendorIDs', params.groupVendorIDs.join(','));
        if (params.categoryCode) sp.set('categoryCode', params.categoryCode);
        if (params.search) sp.set('search', params.search);
        const res = await fetch(`/api/v1/vendor-cost-analysis/transactions/export?${sp}`);
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        downloadBlob(await res.blob(), `VendorCostTransactions_${todayStamp()}.xlsx`);
    },

    // Transaction Comments
    async getComments(transactionRefs: string[]): Promise<VCAComment[]> {
        if (transactionRefs.length === 0) return [];
        const sp = new URLSearchParams();
        sp.set('transactionRefs', transactionRefs.join('|||'));
        return fetchApi(`/vendor-cost-analysis/comments?${sp}`);
    },

    async createComment(data: {
        transactionRef: string;
        commentText: string;
        periodYear: number;
        periodMonth: number;
        companyCode?: string;
        accountCode?: string;
    }): Promise<VCAComment> {
        const res = await fetch('/api/v1/vendor-cost-analysis/comments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to create comment');
        return body.data;
    },

    async updateComment(id: number, commentText: string): Promise<void> {
        const res = await fetch(`/api/v1/vendor-cost-analysis/comments/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ commentText }),
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to update comment');
    },

    async deleteComment(id: number): Promise<void> {
        const res = await fetch(`/api/v1/vendor-cost-analysis/comments/${id}`, { method: 'DELETE' });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to delete comment');
    },
};
